import { Badge, cn } from "@dl/web-core-js/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ProductionOrderComponentDTO, ProductionOrderOperationDTO } from "../../../api/floor";
import { formatNumber } from "../../../api/manufacturing";
import type { ProductionOrderDTO } from "../../../api/production";
import { percent, ProgressBar } from "../../../components/ProgressBar";
import { countsCutboards, lotSteps, matrixRows, processColumns, stepShare, stepTone } from "./lotModel";
import { stepLabel } from "../assembly";
import { StepDialog } from "./StepDialog";

/**
 * The lot at a glance: one row per component, one column per process it goes
 * through, each cell how far that step is (in its own unit: cutboards for a
 * nested part at Cut and Edge). Green done, blue under way, amber stalled, red
 * with parts missing, hatched skipped, grey not started. A cell opens the step:
 * its history and the reports the floor sends. An assembly order has no
 * components: its own steps at Terrebonne (kit, foam, cornières, packing per
 * box) are the whole of it.
 */
export function OverviewTab({
  lot,
  components,
  steps,
  loading,
  editable,
  onChanged,
}: {
  lot: ProductionOrderDTO;
  components: ProductionOrderComponentDTO[];
  steps: ProductionOrderOperationDTO[];
  loading: boolean;
  editable: boolean;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState<number | null>(null);
  const open = openId == null ? null : (steps.find((step) => step.id === openId) ?? null);

  const own = lotSteps(steps);
  if (lot.status === "PLANNED") {
    return (
      <p className="text-[13px] text-ink-muted">
        {lot.kind === "ASSEMBLY"
          ? t("lot.overview.planned.assembly", "Its steps are written when its first sets arrive at Terrebonne: the hardware kit, the foam, the cornières, then packing for each box.")
          : t("lot.overview.planned.v2", "The floor plan is written when the lot is released: its components, each with the steps of its routing.")}
      </p>
    );
  }
  if (lot.kind === "ASSEMBLY") {
    return (
      <div className="flex flex-col gap-4">
        {own.length === 0 ? (
          <p className="text-[13px] text-ink-muted">{t("lot.overview.assembly.none", "No step yet: they are written when its first sets arrive.")}</p>
        ) : (
          <AssemblySteps steps={own} onOpen={setOpenId} />
        )}
        <Legend />
        {open && <StepDialog step={open} editable={editable} onClose={() => setOpenId(null)} onChanged={onChanged} />}
      </div>
    );
  }
  if (!loading && components.length === 0) {
    return (
      <p className="text-[13px] text-ink-muted">
        {lot.is_from_sigm
          ? t("lot.overview.sigm", "This run was imported from SIGM and is not followed on nexpim's floor. Its component runs are on their own tab.")
          : t("lot.overview.none", "This lot has no components followed on the floor.")}
      </p>
    );
  }

  const columns = processColumns(steps);
  const rows = matrixRows(components, steps);

  return (
    <div className="flex flex-col gap-4">
      <div className="overflow-x-auto rounded-md border border-line">
        <table className="w-full min-w-[720px] text-[13px]">
          <thead className="bg-surface-sunk text-left text-[11px] uppercase tracking-wide text-ink-muted">
            <tr>
              <th className="px-3 py-2 font-medium">{t("lot.overview.component", "Component")}</th>
              <th className="px-3 py-2 text-right font-medium">{t("column.mo.quantity", "Quantity")}</th>
              {columns.map((column) => (
                <th key={column.code} className="px-3 py-2 font-medium">
                  {column.name}
                </th>
              ))}
              <th className="px-3 py-2 font-medium">{t("lot.overview.progress", "Progress")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ component, steps: byProcess }) => (
              <tr key={component.id} className="border-t border-line align-top">
                <td className="px-3 py-2">
                  <div className="flex items-baseline gap-2">
                    <span className="whitespace-nowrap font-mono text-[12px]">{component.code}</span>
                    {component.letter && (
                      <Badge variant="accent" size="sm">
                        {component.letter}
                      </Badge>
                    )}
                    {component.has_newer_drawing && (
                      <Badge variant="warning" size="sm">
                        {t("lot.component.newer-drawing", "newer drawing")}
                      </Badge>
                    )}
                  </div>
                  <div className="text-[12px] text-ink-muted">
                    {component.part_number} <span className="text-ink-faint">{component.part_name}</span>
                  </div>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatNumber(component.quantity)}
                  {component.parts_per_cutboard != null && component.parts_per_cutboard > 1 && (
                    <div className="text-[11px] text-ink-faint">
                      {t("lot.overview.cutboards", "{{n}} cutboards", { n: component.cutboard_quantity ?? "" })}
                    </div>
                  )}
                  {(component.pallet_count ?? 1) > 1 && (
                    <div className="text-[11px] text-ink-faint">{t("lot.overview.pallets", "{{n}} pallets", { n: component.pallet_count })}</div>
                  )}
                </td>
                {columns.map((column) => {
                  const step = byProcess.get(column.code);
                  return (
                    <td key={column.code} className="px-2 py-1.5">
                      {step ? <StepCell step={step} onOpen={() => setOpenId(step.id)} /> : <span className="text-ink-faint">·</span>}
                    </td>
                  );
                })}
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <ProgressBar value={component.progress} tone={(component.progress ?? 0) >= 1 ? "done" : "active"} className="w-16" />
                    <span className="text-[12px] tabular-nums text-ink-muted">{percent(component.progress)}</span>
                  </div>
                  {Number(component.quantity_missing ?? 0) > 0 && (
                    <Badge variant="error" size="sm" className="mt-1">
                      {t("lot.overview.missing", "{{n}} missing", { n: formatNumber(component.quantity_missing) })}
                    </Badge>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Legend />

      {open && <StepDialog step={open} editable={editable} onClose={() => setOpenId(null)} onChanged={onChanged} />}
    </div>
  );
}

/**
 * An assembly order's steps top to bottom, as Terrebonne works them: the
 * preparations (hardware kit, foam, cornières) in any order, then packing, box
 * by box, which can only do what all three have made ready. Each row opens the
 * step.
 */
function AssemblySteps({ steps, onOpen }: { steps: ProductionOrderOperationDTO[]; onOpen: (id: number) => void }) {
  const { t } = useTranslation();
  const ordered = [...steps].sort((a, b) => a.position - b.position);
  const prepare = ordered.filter((step) => step.process_code !== "PACK");
  const pack = ordered.filter((step) => step.process_code === "PACK");
  const groups: Array<{ title: string; hint: string; steps: ProductionOrderOperationDTO[] }> = [
    { title: t("lot.assembly.prepare", "Prepare"), hint: t("lot.assembly.prepare.hint", "In any order"), steps: prepare },
    { title: t("lot.assembly.pack", "Then pack"), hint: t("lot.assembly.pack.hint", "Box by box, as many as are ready on every step above"), steps: pack },
  ].filter((group) => group.steps.length > 0);
  let number = 0;
  return (
    <div className="flex max-w-3xl flex-col gap-4">
      {groups.map((group) => (
        <section key={group.title} className="flex flex-col">
          <h4 className="mb-1 flex items-baseline gap-2 text-[12px] font-semibold uppercase tracking-wide text-ink-muted">
            {group.title}
            <span className="font-normal normal-case tracking-normal text-ink-faint">{group.hint}</span>
          </h4>
          <ol className="flex flex-col">
            {group.steps.map((step, index) => {
              number++;
              const tone = stepTone(step);
              const last = index === group.steps.length - 1;
              return (
                <li key={step.id} className="flex gap-3">
                  <span className="flex flex-col items-center">
                    <span
                      className={cn(
                        "mt-2 flex size-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold tabular-nums",
                        tone === "done" ? "bg-ok text-ink-on-accent" : tone === "active" ? "bg-info text-ink-on-accent" : tone === "risk" ? "bg-risk text-ink-on-accent"
                          : tone === "warn" ? "bg-warn text-ink-on-accent" : "bg-surface-sunk text-ink-muted",
                      )}
                    >
                      {number}
                    </span>
                    {!last && <span className="w-px flex-1 bg-line" />}
                  </span>
                  <div className="mb-2 flex min-w-0 flex-1 items-center gap-3 rounded-md border border-line pl-3">
                    <span className="w-40 shrink-0 text-[13px] font-medium">{stepLabel(t, step.process_name, step.box_number)}</span>
                    <div className="min-w-0 flex-1">
                      <StepCell step={step} onOpen={() => onOpen(step.id)} wide />
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </section>
      ))}
    </div>
  );
}

/** One step as a button: its bar and done over needed. */
function StepCell({ step, onOpen, wide = false }: { step: ProductionOrderOperationDTO; onOpen: () => void; wide?: boolean }) {
  const { t } = useTranslation();
  const tone = stepTone(step);
  const required = step.quantity_required_counted ?? step.quantity_required;
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "flex w-full flex-col gap-1 rounded-md px-2 py-1.5 text-left hover:bg-surface-sunk focus-visible:outline-2 focus-visible:outline-accent",
        wide ? "min-w-48" : "min-w-28",
      )}
      title={step.station_codes ?? undefined}
    >
      <ProgressBar value={stepShare(step)} tone={tone} />
      <span className="flex items-baseline gap-1 text-[12px] tabular-nums">
        {step.status === "SKIPPED" ? (
          <span className="text-ink-faint">{t("floor.step.skipped", "Skipped")}</span>
        ) : (
          <>
            <span className={tone === "done" ? "text-ok" : undefined}>{formatNumber(step.quantity_done ?? 0)}</span>
            <span className="text-ink-faint">/ {formatNumber(required)}</span>
            {countsCutboards(step) && <span className="text-[10px] text-ink-faint">{t("lot.unit.cb", "cb")}</span>}
          </>
        )}
        {Number(step.quantity_scrap ?? 0) > 0 && (
          <span className="ml-auto text-[11px] text-warn">{t("lot.overview.scrap-short", "-{{n}}", { n: formatNumber(step.quantity_scrap) })}</span>
        )}
        {step.has_override && (
          <span className={cn("text-[11px] font-semibold text-warn", Number(step.quantity_scrap ?? 0) > 0 ? undefined : "ml-auto")} title={t("lot.overview.override-hint", "Something was reported past what the step allowed: see its history.")}>
            !
          </span>
        )}
      </span>
      {step.is_external && step.status !== "SKIPPED" && (
        <span className="text-[11px] tabular-nums text-ink-muted">
          {t("lot.overview.sent-back", "sent {{sent}} · back {{back}}", { sent: formatNumber(step.quantity_sent ?? 0), back: formatNumber(step.quantity_done ?? 0) })}
        </span>
      )}
      {(Number(step.quantity_missing ?? 0) > 0 || step.is_stalled) && (
        <span className={cn("text-[11px]", Number(step.quantity_missing ?? 0) > 0 ? "text-risk" : "text-warn")}>
          {Number(step.quantity_missing ?? 0) > 0
            ? t("lot.overview.missing", "{{n}} missing", { n: formatNumber(step.quantity_missing) })
            : t("lot.overview.stalled", "stalled")}
        </span>
      )}
    </button>
  );
}

function Legend() {
  const { t } = useTranslation();
  const items: Array<[string, string]> = [
    ["bg-ink-faint", t("floor.step.pending", "To do")],
    ["bg-info", t("floor.step.active", "In progress")],
    ["bg-ok", t("floor.step.done", "Done")],
    ["bg-warn", t("lot.overview.stalled", "stalled")],
    ["bg-risk", t("lot.overview.legend-missing", "parts missing")],
  ];
  return (
    <div className="flex flex-wrap gap-4 text-[12px] text-ink-muted">
      {items.map(([color, label]) => (
        <span key={label} className="flex items-center gap-1.5">
          <span className={cn("inline-block size-2.5 rounded-full", color)} />
          {label}
        </span>
      ))}
      <span>{t("lot.overview.cb-legend", "cb: cutboards (nested parts, counted by board at Cut and Edge)")}</span>
      <span>{t("lot.overview.override-legend", "!: reported past what the step allowed (see its history)")}</span>
    </div>
  );
}
